import React from 'react';
import {noop} from 'lodash';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@packages/systems/spring';
import {
  animations,
  emotionStyled,
  keyframes,
  reducedMotionAtom,
} from '@packages/utilities/styled';

import {
  DEFAULT_EMPTY_TEXT,
  StyleChangesList,
  type StyleChangesElementGroup,
  type StyleChangesListLayout,
} from './StyleChangesList';

const DraftStylesPopoverHeader = emotionStyled(PopoverHeader)({
  '& [data-wf-icon="CloseDefaultIcon"]': {
    color: '$textSecondary',
  },
});

const revealToolbarPopover = keyframes({
  from: {gridTemplateRows: '0fr'},
  to: {gridTemplateRows: '1fr'},
});

const hideToolbarPopover = keyframes({
  from: {gridTemplateRows: '1fr'},
  to: {gridTemplateRows: '0fr'},
});

// These styles land on the popover *wrapper* (PopoverContent forwards its
// className there), so `display: grid` wins over the wrapper's flex and the
// reveal animation squeezes the single content child.
const ToolbarPopoverContent = emotionStyled(PopoverContent)(
  {
    animation: `${revealToolbarPopover} ${animations.durationSlow} ${animations.ease}`,
    animationFillMode: 'both',
    backgroundColor: '$background1',
    boxSizing: 'border-box',
    display: 'grid',
    overflow: 'hidden',
    paddingTop: '$1',
    transformOrigin: 'bottom',
    '& > div': {
      minHeight: 0,
      overflow: 'hidden',
    },
    [`&& ${PopoverBody}`]: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
    },
    '&[data-toolbar-popover-open="false"]': {
      animationName: hideToolbarPopover,
    },
  },
  {
    composes: [reducedMotionAtom],
  }
);

const ToolbarPopoverHeader = emotionStyled(DraftStylesPopoverHeader)({
  padding: '$1 $2',
});

type PopoverComponentProps = React.ComponentProps<typeof Popover>;
type PopoverProps = Omit<PopoverComponentProps, 'children'>;

interface StyleChangesPopoverProps {
  groups: StyleChangesElementGroup[];
  children: React.ReactNode;
  onRemoveGroup?: (nodeId: string, variantId: string | undefined) => void;
  emptyText?: string;
  popoverProps?: PopoverProps;
  contentAutomationId?: string;
  headerTitle?: string;
  headerSuffix?: React.ReactNode;
  hasCloseButton?: boolean;
  layout?: StyleChangesListLayout;
}

export const StyleChangesPopover = ({
  groups,
  children,
  onRemoveGroup = noop,
  emptyText = DEFAULT_EMPTY_TEXT,
  popoverProps,
  contentAutomationId = 'style-changes-popover',
  headerTitle = 'Draft changes',
  headerSuffix,
  hasCloseButton = true,
  layout = 'default',
}: StyleChangesPopoverProps) => {
  const Content = layout === 'toolbar' ? ToolbarPopoverContent : PopoverContent;
  const Header =
    layout === 'toolbar' ? ToolbarPopoverHeader : DraftStylesPopoverHeader;

  return (
    <Popover {...popoverProps}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <Content
        aria-label={headerTitle}
        colorVariant={layout === 'toolbar' ? 'default' : 'secondary'}
        data-automation-id={contentAutomationId}
        data-toolbar-popover-open={
          layout === 'toolbar'
            ? String(Boolean(popoverProps?.isOpen))
            : undefined
        }
        shouldAnimate={layout === 'toolbar'}
      >
        <Header
          title={headerTitle}
          suffix={headerSuffix}
          hasCloseButton={hasCloseButton}
          data-automation-id={`${contentAutomationId}-header`}
        />
        <PopoverBody padding="$0">
          <StyleChangesList
            groups={groups}
            emptyText={emptyText}
            layout={layout}
            onRemoveGroup={onRemoveGroup}
          />
        </PopoverBody>
      </Content>
    </Popover>
  );
};
